import { Routes } from '@common/constants';
import { useSinglePageCheckoutSupported } from '@common/hooks/useSinglePageCheckoutSupported';
import BasketContainer from '@features/Basket/containers/Basket';
import { BasketV2Connected } from '@features/Basket/v2';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useTranslations } from '@lang';
import { fetchBasketDetails } from '@store/basket/actions';
import { NormalizedStore } from '@store/entity/entity.types';
import { getSettings } from '@store/setting/actions';
import Cookie from 'js-cookie';
import { NextPage, NextPageContext } from 'next';
import NextSeo from 'next-seo';
import Head from 'next/head';
import { useEffect } from 'react';
import { Image } from 'shared-types/Common';
import { Property } from 'shared-types/Property';

type BasketProps = {
  language: string;
  propertyId: number;
  propertyTitle: string;
  socialFallbackImage: Image;
  url: string;
};

const Basket: NextPage<BasketProps> = ({
  language,
  propertyId,
  propertyTitle,
  socialFallbackImage,
  url,
}) => {
  const { t } = useTranslations();
  const isEcom2 = useFeatureIsOn('feature.ecom2');
  const isSinglePageCheckoutSupported = useSinglePageCheckoutSupported();

  const checkoutRoute: string = isSinglePageCheckoutSupported
    ? Routes.CHECKOUT
    : Routes.CHECKOUT_SIGNIN;

  useEffect(() => {
    if (isSinglePageCheckoutSupported) {
      Cookie.set('condensedCheckout', '1', { path: '/' });
    } else {
      Cookie.remove('condensedCheckout', { path: '/' });
    }
  }, [isSinglePageCheckoutSupported]);

  const pageName = t('app.basket');

  const pageTitle = propertyTitle ? `${pageName} | ${propertyTitle}` : pageName;

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <NextSeo
        config={{
          openGraph: {
            title: pageTitle,
            locale: language,
            url,
            images: [{ url: socialFallbackImage && socialFallbackImage.url }],
          },
        }}
      />
      {isEcom2 ? (
        <BasketV2Connected
          language={language}
          checkoutRoute={checkoutRoute}
          isSinglePageCheckout={isSinglePageCheckoutSupported}
        />
      ) : (
        <BasketContainer
          language={language}
          propertyId={propertyId}
          checkoutRoute={checkoutRoute}
          isSinglePageCheckout={isSinglePageCheckoutSupported}
        />
      )}
    </>
  );
};

type BasketInitialProps = {
  property: Property;
  store: NormalizedStore;
  language: string;
  url: string;
} & NextPageContext;

Basket.getInitialProps = async ({ store, property, language, url }: BasketInitialProps) => {
  const basketId = Cookie.get('basketId') as unknown as number;
  await Promise.all([
    ...(basketId ? [fetchBasketDetails(property.id, basketId, language)(store.dispatch)] : []),
    getSettings()(store.dispatch),
  ]);
  return {
    url,
    language,
    propertyId: property.id,
    propertyTitle: property.propertyTitle,
    socialFallbackImage: property.socialFallbackImage,
  };
};

export default Basket;
